import React, { useState } from 'react'
import styled from 'styled-components'
import Select from 'react-select'
import { useApi } from '../../hooks/useApi'
import CustomInput from '../general/Form'

interface FormProps {
  onSuccess?: () => void
}

const SignUpForm: React.FC<FormProps> = ({ onSuccess = () => {} }) => {
  const { callApi } = useApi()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [roles, setRoles] = useState<string[]>([])

  const rolesList = [
    { value: 'viewer', label: 'viewer' },
    { value: 'maintainer', label: 'maintainer' },
    { value: 'manager', label: 'manager' },
    { value: 'root', label: 'root' },
  ]

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await callApi('/admin/users', { email, password, roles }, 'POST')
      onSuccess()
    } catch {}
  }

  const handleSelectRoles = (e: any) => {
    setRoles(e?.map((item: any) => item.value) || [])
  }

  const showOptions = (list: string[]) => list.map((o) => ({ value: o, label: o }))

  return (
    <Form onSubmit={handleFormSubmit}>
      {message !== '' ? (
        <FeedbackBanner>
          <span>{message}</span>
          <CloseBtn onClick={() => setMessage('')}>✕</CloseBtn>
        </FeedbackBanner>
      ) : (
        <>
          <FormTitle>New user</FormTitle>
          <FieldGrid>
            <CustomInput
              type="email"
              label="Email"
              name="Email"
              modal
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              required
            />
            <CustomInput
              type="password"
              label="Password"
              name="Password"
              modal
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              pattern="^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$"
              title="Min 8 chars, with symbol, upper/lower case, and number"
              required
            />
            <div>
              <FieldLabel>Roles</FieldLabel>
              <StyledSelect
                classNamePrefix="Select"
                isMulti
                value={showOptions(roles)}
                onChange={handleSelectRoles}
                options={rolesList}
              />
            </div>
          </FieldGrid>
          <SubmitBtn type="submit">Create user</SubmitBtn>
        </>
      )}
    </Form>
  )
}

export default SignUpForm

// ─── Styles ──────────────────────────────────────────────────────────────────

const Form = styled.form`display: flex; flex-direction: column; gap: 16px;`

const FormTitle = styled.h2`
  font-size: 14px; font-weight: 600; color: #ededed; margin: 0;
`

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 8px;
  align-items: start;
`

const FieldLabel = styled.label`
  display: block;
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.05em; color: #777; margin-bottom: 6px;
`

const SubmitBtn = styled.button`
  height: 34px; padding: 0 16px; align-self: flex-start;
  background: rgba(111,92,230,0.15); border: 1px solid rgba(111,92,230,0.35);
  border-radius: 6px; color: #a89aff; font-size: 13px; font-weight: 500; cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover { background: rgba(111,92,230,0.25); border-color: rgba(111,92,230,0.5); }
`

const FeedbackBanner = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; background: rgba(51,194,176,0.08); border: 1px solid rgba(51,194,176,0.25);
  border-radius: 6px; color: #33C2B0; font-size: 13px;
`

const CloseBtn = styled.button`
  background: transparent; border: none; color: inherit; cursor: pointer; font-size: 12px; opacity: 0.7;
  &:hover { opacity: 1; }
`

const StyledSelect = styled(Select)`
  .Select__control {
    background: #0d0d0d;
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 6px;
    min-height: 34px;
    box-shadow: none;
    cursor: pointer;
    &:hover { border-color: rgba(255,255,255,0.15); }
    &.Select__control--is-focused { border-color: rgba(111,92,230,0.5); }
  }
  .Select__value-container { padding: 0 10px; }
  .Select__placeholder { color: #a1a1a1; font-size: 13px; }
  .Select__input-container { color: #ededed; font-size: 13px; }
  .Select__multi-value { background: rgba(111,92,230,0.15); border: 1px solid rgba(111,92,230,0.3); border-radius: 4px; }
  .Select__multi-value__label { color: #a89aff; font-size: 12px; }
  .Select__multi-value__remove { color: #a89aff; cursor: pointer; &:hover { background: rgba(111,92,230,0.25); } }
  .Select__indicator-separator { background: rgba(255,255,255,0.08); }
  .Select__dropdown-indicator { color: #777; &:hover { color: #a0a0a0; } }
  .Select__menu {
    background: #161616; border: 1px solid rgba(255,255,255,0.07); border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.4); z-index: 10;
  }
  .Select__option {
    background: transparent; color: #a0a0a0; font-size: 13px; cursor: pointer;
    &:hover, &.Select__option--is-focused { background: rgba(255,255,255,0.04); color: #ededed; }
    &.Select__option--is-selected { background: rgba(111,92,230,0.15); color: #a89aff; }
  }
`
